import type { TFunction } from 'i18next';
import {
  Calendar, Clock, CreditCard, Flag, LifeBuoy, MessageSquare, Settings, ShieldCheck, CheckSquare, Timer, type LucideIcon,
} from 'lucide-react';
import { formatDate, formatNumber, formatYearMonth } from '../utils/format';
import { monthReopenedParams } from '../utils/monthlyApprovalModel';
import { isReminder } from '../utils/reminderModel';
import type { Notification } from '../types';

/**
 * FIX-NOTIF N-1 - ONE place that decides how a notification looks: its kind
 * (icon), its tone (colour) and its one-line title. The drawer's row, the
 * portal overview's "recent notifications" and the toast all read from here.
 */
export type NotificationKind =
  | 'ticket' | 'chat' | 'task' | 'milestone' | 'money' | 'hours' | 'approval' | 'meeting' | 'timer' | 'system';

/** Q1: only what matters is coloured - a debt, an approval, a delay. Everything else stays grey. */
export type NotificationTone = 'neutral' | 'danger' | 'warning';

export const KIND_ICON: Record<NotificationKind, LucideIcon> = {
  ticket: LifeBuoy,
  chat: MessageSquare,
  task: CheckSquare,
  milestone: Flag,
  money: CreditCard,
  hours: Clock,
  approval: ShieldCheck,
  meeting: Calendar,
  timer: Timer,
  system: Settings,
};

const APPROVAL = new Set([
  'ticket_approval_requested', 'ticket_approval_reminder', 'ticket_approval_decided',
  'overtime_approval_requested', 'overtime_approval_granted', 'overtime_approval_rejected',
  'lll_hours_approval_requested', 'lll_hours_decided', 'billing_pending_approval', 'month_approval_required',
]);
const CHAT = new Set(['comment_added', 'conversation_started']);
const HOURS = new Set([
  'hour_bank_threshold', 'hour_bank_exhausted', 'hour_bank_report_sent', 'hour_bank_report_received',
  'service_contract_80', 'over_quota_hours_logged', 'over_quota_hours_charged', 'project_hours_threshold',
]);
const MILESTONE = new Set([
  'milestone_assigned', 'project_assigned', 'milestone_approaching', 'milestone_overdue', 'milestone_completed',
  'project_completed', 'project_at_risk', 'project_warranty_needed', 'warranty_ending', 'engagement_archived', 'engagement_purged',
]);
const DANGER = new Set([
  'debt_due', 'customer_debt_work', 'deadline_overdue', 'task_overdue', 'milestone_overdue',
  'sla_response_breached', 'sla_resolution_breached', 'billing_recurring_failed', 'timer_midnight_stop_failed',
]);
const WARNING = new Set(['ticket_approval_requested', 'ticket_approval_reminder', 'overtime_approval_requested', 'lll_hours_approval_requested', 'hour_bank_exhausted', 'stale_ticket']);

export function notificationKind(eventType: string): NotificationKind {
  if (APPROVAL.has(eventType)) return 'approval';
  if (CHAT.has(eventType)) return 'chat';
  if (HOURS.has(eventType)) return 'hours';
  if (MILESTONE.has(eventType)) return 'milestone';
  if (eventType.startsWith('task_') || eventType.startsWith('deadline_') || eventType === 'custom_reminder') return 'task';
  if (eventType.startsWith('meeting_')) return 'meeting';
  if (eventType.startsWith('timer_') || eventType.startsWith('idle_') || eventType.startsWith('attendance_') || eventType.startsWith('service_log_')) return 'timer';
  if (eventType.startsWith('ticket_') || eventType === 'stale_ticket') return 'ticket';
  if (eventType.startsWith('sla_')) return 'ticket';
  if (eventType.startsWith('invoice_') || eventType.startsWith('payment_') || eventType.startsWith('debt_') || eventType.startsWith('billing_') || eventType.startsWith('quote_') || eventType.startsWith('customer_debt')) return 'money';
  return 'system';
}

export function notificationTone(eventType: string): NotificationTone {
  if (DANGER.has(eventType)) return 'danger';
  if (WARNING.has(eventType)) return 'warning';
  return 'neutral';
}

/** The one-line title of a notification (was `rowTitle` inside NotificationCenter). */
export function describeNotification(n: Notification, t: TFunction): string {
  if (isReminder(n)) return String(n.metadata?.message ?? t('notifications.custom_reminder'));
  if (n.event_type === 'timer_midnight_stop_failed') {
    return t('notifications.midnightStopFailedLine', { name: String(n.metadata?.employee_name ?? ''), title: String(n.metadata?.ticket_title ?? '') });
  }
  if (n.event_type === 'timer_billed_month_hours') {
    return t('notifications.billedMonthHoursLine', { title: String(n.metadata?.ticket_title ?? ''), minutes: Number(n.metadata?.minutes ?? 0), chargeMonth: String(n.metadata?.charge_month ?? '') });
  }
  if (n.event_type === 'timer_long_running') {
    return t('replyTimer.longRun.title', { title: String(n.metadata?.ticket_title ?? ''), hours: Number(n.metadata?.hours ?? 8) });
  }
  // P-06/C (D-59) — "צורפת למשימה '<שם>' בפרויקט/בהתקשרות '<שם>'".
  if (n.event_type === 'task_assigned' && n.metadata?.container_name) {
    return t('notifications.taskAssignedLine', {
      task: String(n.metadata?.ticket_title ?? ''),
      where: t(n.metadata?.container_kind === 'project' ? 'notifications.inProject' : 'notifications.inEngagement'),
      container: String(n.metadata.container_name),
    });
  }
  // P-56 (D-231 §3) — "צורפת לאבן הדרך / לפרויקט '<שם>'".
  if ((n.event_type === 'milestone_assigned' || n.event_type === 'project_assigned') && n.metadata?.container_name) {
    return t(n.event_type === 'milestone_assigned' ? 'notifications.milestoneAssignedLine' : 'notifications.projectAssignedLine', { name: String(n.metadata.container_name) });
  }
  // SEC-2b (D-106) — "העובד X יצר לעצמו את המשימה Y (וצירף אליה את ...)".
  if (n.event_type === 'task_self_created' && n.metadata?.actor_name) {
    const added = Array.isArray(n.metadata?.added_names) ? (n.metadata.added_names as unknown[]).map(String).filter(Boolean) : [];
    const params = { name: String(n.metadata.actor_name), task: String(n.metadata?.ticket_title ?? ''), added: added.join(', ') };
    return added.length > 0 ? t('notifications.taskSelfCreatedWith', params) : t('notifications.taskSelfCreated', params);
  }
  // P-10a (D-65) — "<שם> סימן/ה את המשימה כהושלמה".
  if (n.event_type === 'task_completed_by_other' && n.metadata?.actor_name) {
    // D-99 (QX-34) — a service call closed the same way.
    return t(n.metadata?.task_type === 'ticket' ? 'notifications.ticketClosedByOther' : 'notifications.taskCompletedByOther', { name: String(n.metadata.actor_name) });
  }
  // D-99 (QX-33) — "<שם> ביטל/ה את המשימה" (my timer on it was stopped).
  if (n.event_type === 'task_cancelled_by_other' && n.metadata?.actor_name) {
    return t(n.metadata?.task_type === 'ticket' ? 'notifications.ticketCancelledByOther' : 'notifications.taskCancelledByOther', { name: String(n.metadata.actor_name) });
  }
  // FU-30 (D-101) — "<מי> הוסיף/ה את <שם> לפורטל של <לקוח>".
  if (n.event_type === 'portal_user_added' && n.metadata?.user_name) {
    const params = {
      who: String(n.metadata?.added_by_name ?? ''),
      name: String(n.metadata.user_name),
      org: String(n.metadata?.organization_name ?? ''),
    };
    return params.who ? t('notifications.portalUserAddedLine', params) : t('notifications.portalUserAddedLineNoActor', params);
  }
  // FU-30 (D-69) — "לא הוגדרה מנהלת חשבונות — הדוח של <עובד> ל<חודש> לא נשלח".
  if (n.event_type === 'attendance_accountant_missing' && typeof n.metadata?.month_year === 'string') {
    const name = typeof n.metadata?.employee_name === 'string' ? n.metadata.employee_name : '';
    const month = formatYearMonth(n.metadata.month_year.slice(0, 7));
    return name
      ? t('notifications.accountantMissingLine', { name, month })
      : t('notifications.accountantMissingLineNoName', { month });
  }
  // FIX-ATT — the report could not be e-mailed to the accountant (SMTP / address).
  if (n.event_type === 'attendance_accountant_failed' && typeof n.metadata?.month_year === 'string') {
    const name = typeof n.metadata?.employee_name === 'string' ? n.metadata.employee_name : '';
    const month = formatYearMonth(n.metadata.month_year.slice(0, 7));
    // D-318 - the mail went out, only the PDF is missing.
    if (n.metadata?.reason === 'pdf_failed') return t('notifications.accountantPdfFailedLine', { name, month });
    return t('notifications.accountantFailedLine', { name, month });
  }
  // D-265 sec.2-3 — "the engagement X was archived / deleted for good".
  if ((n.event_type === 'engagement_archived' || n.event_type === 'engagement_purged') && n.metadata?.engagement_name) {
    return t(n.event_type === 'engagement_archived' ? 'notifications.engagementArchivedLine' : 'notifications.engagementPurgedLine', { name: String(n.metadata.engagement_name) });
  }
  // D-329 — an employee asked to complete an engagement's financial data (no sums).
  if (n.event_type === 'engagement_financials_requested' && n.metadata?.engagement_name) {
    return t('notifications.financialsRequestedLine', {
      who: String(n.metadata.requested_by_name ?? ''),
      name: String(n.metadata.engagement_name),
      customer: String(n.metadata.organization_name ?? ''),
    });
  }
  // D-331 QX-2 — a new customer waits for an Account Manager (no sums).
  if (n.event_type === 'customer_needs_account_manager' && n.metadata?.organization_name) {
    return t('notifications.customerNeedsAmLine', { customer: String(n.metadata.organization_name), who: String(n.metadata.requested_by_name ?? '') });
  }
  // D-331 Q3 — an employee asked to complete the financial data of a customer opened without them (no sums).
  if (n.event_type === 'customer_financials_requested' && n.metadata?.organization_name) {
    return t('notifications.customerFinancialsRequestedLine', {
      who: String(n.metadata.requested_by_name ?? ''),
      customer: String(n.metadata.organization_name),
    });
  }
  // P-30b (D-171 / Q-144) — "the hours of project X reached 80% / 100%".
  if (n.event_type === 'project_hours_threshold' && n.metadata?.project_name) {
    return t('notifications.projectHoursLine', {
      project: String(n.metadata.project_name),
      pct: Number(n.metadata?.threshold_pct ?? 0),
    });
  }
  // D-303 (round 2) — a project ended by itself: "fill in the warranty".
  if (n.event_type === 'project_warranty_needed' && n.metadata?.project_name) {
    return t('notifications.projectWarrantyNeededLine', { project: String(n.metadata.project_name) });
  }
  // D-303 — "האחריות ללקוח <שם> מסתיימת ב-<תאריך>" (+ the "התקשרות חדשה" button under the row).
  if (n.event_type === 'warranty_ending' && n.metadata?.warranty_until) {
    return t('notifications.warrantyEndingLine', {
      customer: String(n.metadata?.organization_name ?? n.organization_name ?? ''),
      date: formatDate(String(n.metadata.warranty_until)),
    });
  }
  // FIX-D300-S2 (D-300 sec.3-5) — a request of a customer with no coverage (to the managers) and the decision (to the sender).
  if (n.event_type === 'coverage_request_created' && n.metadata?.ticket_title) {
    return t('coverage.managerMessage', { customer: String(n.metadata?.organization_name ?? ''), subject: String(n.metadata.ticket_title) });
  }
  if (n.event_type === 'coverage_request_resolved') {
    const r = String(n.metadata?.resolution ?? '');
    return r === 'approved' ? t('coverage.resolvedApproved')
      : t('coverage.resolvedAdded', { what: t(r === 'hour_bank' ? 'coverage.whatBank' : r === 'service_contract' ? 'coverage.whatContract' : 'coverage.whatLll') });
  }
  // D-273 — to the customer: a hour-bank breakdown was sent to you for a period.
  if (n.event_type === 'hour_bank_report_received' && n.metadata?.period_start) {
    return t('notifications.hourBankReportReceivedLine', { from: formatDate(String(n.metadata.period_start)), to: formatDate(String(n.metadata.period_end ?? n.metadata.period_start)) });
  }
  // Website leads (10-29) — "ליד חדש מאתר: X" (to the OTE managers).
  if (n.event_type === 'lead_created' && n.metadata?.company_name) {
    return n.metadata.source_label
      ? t('notifications.leadCreatedLine', { company: String(n.metadata.company_name), source: String(n.metadata.source_label) })
      : t('notifications.leadCreatedNoSourceLine', { company: String(n.metadata.company_name) });
  }
  // P-46 (D-216 s.6) — "הליד X שויך אליך" / "היום יום המעקב אחרי הליד X".
  if ((n.event_type === 'lead_assigned' || n.event_type === 'lead_follow_up_due') && n.metadata?.company_name) {
    return t(n.event_type === 'lead_assigned' ? 'notifications.leadAssignedLine' : 'notifications.leadFollowUpLine', { company: String(n.metadata.company_name) });
  }
  // D-110 — work started for a customer with an overdue debt.
  if (n.event_type === 'customer_debt_work' && n.metadata?.organization_name) {
    return t('notifications.customerDebtWorkLine', {
      name: String(n.metadata.organization_name),
      amount: formatNumber(Number(n.metadata?.amount ?? 0), 0),
      count: Number(n.metadata?.days_overdue ?? 0),
    });
  }
  // D-341: a new improvement suggestion says so (a bug keeps "דיווח באג חדש").
  if (n.event_type === 'bug_report_created' && n.metadata?.report_kind === 'suggestion') return t('notifications.suggestionCreated');
  // D-319 + push — "צריך לאשר את הנוכחות והלוגים של <חודש> לפני שמתחילים לעבוד".
  if (n.event_type === 'month_approval_required' && typeof n.metadata?.month_year === 'string') {
    return t('notifications.monthApprovalRequiredLine', { month: formatYearMonth(n.metadata.month_year.slice(0, 7)) });
  }
  // P-10b (D-69) — "החודש <X> נפתח מחדש: <סיבה>".
  const reopened = n.event_type === 'attendance_monthly_reopened' ? monthReopenedParams(n.metadata) : null;
  if (reopened) {
    return t('notifications.monthReopenedLine', { month: formatYearMonth(reopened.monthKey), reason: reopened.reason });
  }
  // P-10c (D-70) — the monthly service-log notices name their month
  // ("ספטמבר 2026", QA-10c #8); a re-open says so, with its reason (#12).
  // D-319 s.10: "כל הלוגים בשירות ל{חודש} אושרו" names its month the same way.
  if ((n.event_type.startsWith('service_log_') || n.event_type === 'service_logs_all_approved') && typeof n.metadata?.month === 'string') {
    const reason = typeof n.metadata?.reason === 'string' ? n.metadata.reason.trim() : '';
    if (n.event_type === 'service_log_employee_review' && n.metadata?.reopened && reason) {
      return t('notifications.serviceLogReopenedLine', { month: formatYearMonth(n.metadata.month), reason });
    }
    return t(`notifications.serviceLogMonth.${n.event_type}`, {
      month: formatYearMonth(n.metadata.month),
      defaultValue: t(`notifications.${n.event_type}`, { defaultValue: n.event_type }),
    });
  }
  // FIX-APPRLIVE (D-257): the approver's notice names the request and who opened it.
  const approvalMeta = (n.metadata ?? {}) as Record<string, unknown>;
  if (n.event_type === 'ticket_approval_requested' && typeof approvalMeta.title === 'string' && approvalMeta.title) {
    return t('notifications.approvalRequestedLine', { title: approvalMeta.title, name: String(approvalMeta.requester_name || '—') });
  }
  const sender = n.metadata?.sender_name;
  return n.event_type === 'comment_added' && sender ? sender : t(`notifications.${n.event_type}`, { defaultValue: n.event_type });
}
